//Props Validations
import PropTypes from "prop-types";
import ReactECharts from "echarts-for-react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import VariableCategoryColorMap from "utils/VariableCategoryColorMap";

// Styled Tooltip with custom styles
const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
    />
))(() => ({
    '& .MuiTooltip-tooltip': {
        fontSize: '12px',
        maxWidth: '280px',
        backgroundColor: '#333',
        color: '#fff',
        padding: '10px 14px',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        lineHeight: '1.5',
        textAlign: 'left',
    },
    '& .MuiTooltip-arrow': {
        color: '#333',
    },
}));

// Tooltip definitions for the fragrance analysis summary table
const TOOLTIPS = {
    "perc-att-top-half":
        "% of all attributes that fall in the top (more important) half of contributions.",
    "perc-frag-att":
        "% of the input attributes that are fragrance attributes.",
    "top-half-frag-att":
        "% of the top-half (most important) drivers that are fragrance attributes; a high number means fragrance dominates the strongest drivers.",
    "rsq-frag-att":
        "Share of the explained variance attributable specifically to fragrance attributes.",
    "model-frag-p-value":
        "Statistical significance of the fragrance-attribute model.",
    "model-res-p-value":
        "Statistical significance of the overall response model.",
};

function DriverAnalysisHorizontalBarChart({ variableKpi = [], algorithm = "", fragranceAnalysisSummary = null, isFragranceReport = false }) {
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = isHairCare || isSkinCare;
    const isRelativeWeightAnalysis = (algorithm || "").toLowerCase() === "relative_weight_analysis";

    const sortedVariableKpi = [...variableKpi].sort((a, b) => Object.values(a)[0] - Object.values(b)[0]);

    const keys = sortedVariableKpi.map((item) => Object.keys(item)[0]);
    const values = sortedVariableKpi.map((item) => Object.values(item)[0]);

    const customRich = {};
    Object.keys(VariableCategoryColorMap).forEach((ele) => {
        customRich[ele.replace(/[()]/g, "").replace(/\s+/g, "").toLowerCase()] = {
            color: VariableCategoryColorMap[ele],
        };
    });

    const fontSize = keys.length <= 20 ? 13 : 11;

    const getBarColor = (score, dataIndex) => {
        const defaultBlue = "#0060ee";

        // Apply yellow palette for Partial Least Squares AND only for Hair/Skin categories
        const isPartialLeastSquares = (algorithm || "").toLowerCase() === "partial_least_squares";
        const rank = dataIndex / values.length;

        if (isPartialLeastSquares && isBeautyAndWellbeing) {
            if (score > 1) {
                return "#FFD700"; // Yellow
            } else if (score < 1) {
                return "#FFFACD"; // Light Yellow
            }
        } else if (isRelativeWeightAnalysis && isBeautyAndWellbeing) {
            if (rank >= 0.75) return "#D21717";
            if (rank >= 0.5) return "#F59D27";
            if (rank >= 0.25) return "#FFDA00";
            return "#01A801";
        }

        return defaultBlue;
    };

    const options = {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            formatter: function (params) {
                const label = params[0]?.axisValueLabel;
                const [first, second] = label?.split("*-*") || [];
                const color = VariableCategoryColorMap[second] || "black";

                let tooltipText = `<span style=font-size:12px;">${first || label}&nbsp;</span><span style="color: ${color}; font-size:12px;">${second || ""}</span><br/>`;
                const score = params[0].data;
                tooltipText += `${params[0].marker} <span style="float:right; font-weight:bold; padding-left:20px; font-size:12px;">${score}</span><br/>`;

                return tooltipText;
            },
        },
        grid: {
            left: "4%",
            right: "4%",
            bottom: "4%",
            top: "4%",
            containLabel: true,
        },
        xAxis: {
            type: "value",
            boundaryGap: [0, 0.05],
            axisLabel: {
                textStyle: {
                    color: "black",
                    fontSize: 13,
                },
            },
        },
        yAxis: {
            type: "category",
            data: keys,
            axisLabel: {
                interval: 0,
                rich: {
                    ...customRich,
                    default: {
                        color: "black", // Default color for other parts
                    },
                },
                formatter: function (label) {
                    const [_option, kpiData] = label.split("*-*");
                    const colorKey = kpiData?.replace(/[()]/g, "").replace(/\s+/g, "").toLowerCase();
                    return `${_option} {${colorKey}|${kpiData}}`;
                },
                textStyle: {
                    fontSize: fontSize,
                },
            },
        },
        series: [
            {
                type: "bar",
                data: values,
                itemStyle: {
                    // Color based on score
                    color: function (params) {
                        const score = params.data;
                        return getBarColor(score, params.dataIndex);
                    },
                },
                label: {
                    show: true,
                    position: "right",
                    formatter: "{c}",
                    textStyle: {
                        color: "black",
                        fontSize: fontSize - 1,
                    },
                },
            },
        ],
    };

    const RWA_LEGEND = [
        { color: "#D21717", label: "Top 25%" },
        { color: "#F59D27", label: "50%\u201375%" },
        { color: "#FFDA00", label: "25%\u201350%" },
        { color: "#01A801", label: "Bottom 25%" },
    ];

    const displayValue = (value) => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }
        return value;
    };

    // Helper component for tooltip icon
    const TooltipIcon = ({ title }) => (
        <CustomTooltip
            title={title}
            placement="top"
            arrow
            enterDelay={300}
            leaveDelay={100}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'default' }}>
                <InfoOutlinedIcon
                    fontSize="small"
                    style={{
                        color: "#4472C4",
                        fontSize: '16px',
                        marginLeft: '4px',
                    }}
                />
            </span>
        </CustomTooltip>
    );

    TooltipIcon.propTypes = {
        title: PropTypes.string,
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
            }}
        >
            <ReactECharts
                option={options}
                style={{ height: keys.length > 20 ? 900 : 450, width: "100%" }}
            />
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "22px",
                    fontSize: "18px",
                    color: "black",
                }}
            >
                <span style={{ marginRight: "6px" }}>Importance Level</span>
                <ArrowForwardIcon />
            </div>
            {isRelativeWeightAnalysis && isBeautyAndWellbeing && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "24px",
                        marginTop: "22px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                    }}
                >
                    {RWA_LEGEND.map(({ color, label }) => (
                        <div
                            key={label}
                            style={{ display: "flex", alignItems: "center", gap: "6px" }}
                        >
                            <span
                                style={{
                                    display: "inline-block",
                                    width: 16,
                                    height: 16,
                                    borderRadius: "3px",
                                    backgroundColor: color,
                                    flexShrink: 0,
                                }}
                            />
                            <span style={{ fontSize: "18px", color: "black" }}>{label}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Fragrance Analysis Summary table — fragrance driver RWA reports only */}
            {isRelativeWeightAnalysis && isBeautyAndWellbeing && isFragranceReport && fragranceAnalysisSummary && (
                <div style={{ width: "100%", marginTop: "30px", overflowX: "auto" }}>
                    <table
                        style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            border: "1px solid #dee2e6",
                            tableLayout: "fixed",
                        }}
                    >
                        <thead>
                            <tr>
                                <th
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        textAlign: "left",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#495057",
                                        background: "#f8f9fa",
                                        whiteSpace: "nowrap",
                                        width: "16%",
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                                        Analysis
                                    </span>
                                </th>
                                <th
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        textAlign: "left",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#495057",
                                        background: "#f8f9fa",
                                        whiteSpace: "nowrap",
                                        width: "14%",
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                                        Perc Atts Top Half
                                        <TooltipIcon title={TOOLTIPS["perc-att-top-half"]} />
                                    </span>
                                </th>
                                <th
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        textAlign: "left",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#495057",
                                        background: "#f8f9fa",
                                        whiteSpace: "nowrap",
                                        width: "13%",
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                                        Perc Frag Atts
                                        <TooltipIcon title={TOOLTIPS["perc-frag-att"]} />
                                    </span>
                                </th>
                                <th
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        textAlign: "left",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#495057",
                                        background: "#f8f9fa",
                                        whiteSpace: "nowrap",
                                        width: "15%",
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                                        Top Half Frag Atts
                                        <TooltipIcon title={TOOLTIPS["top-half-frag-att"]} />
                                    </span>
                                </th>
                                <th
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        textAlign: "left",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#495057",
                                        background: "#f8f9fa",
                                        whiteSpace: "nowrap",
                                        width: "13%",
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                                        R-Sq Frag Atts
                                        <TooltipIcon title={TOOLTIPS["rsq-frag-att"]} />
                                    </span>
                                </th>
                                <th
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        textAlign: "left",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#495057",
                                        background: "#f8f9fa",
                                        whiteSpace: "nowrap",
                                        width: "15%",
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                                        Model (Frag) P-Value
                                        <TooltipIcon title={TOOLTIPS["model-frag-p-value"]} />
                                    </span>
                                </th>
                                <th
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        textAlign: "left",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#495057",
                                        background: "#f8f9fa",
                                        whiteSpace: "nowrap",
                                        width: "15%",
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                                        Model (Res) P-Value
                                        <TooltipIcon title={TOOLTIPS["model-res-p-value"]} />
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        fontSize: "13px",
                                        color: "#212529",
                                        fontWeight: "bold",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {fragranceAnalysisSummary?.Analysis ||
                                        fragranceAnalysisSummary?.analysis ||
                                        "Overall opinion fragrance shampoo on opening the pack"}
                                </td>
                                <td
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        fontSize: "13px",
                                        color: "#212529",
                                        textAlign: "center",
                                    }}
                                >
                                    {displayValue(fragranceAnalysisSummary?.['Perc Atts Top Half'] ??
                                        fragranceAnalysisSummary?.percAttsTopHalf)}
                                </td>
                                <td
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        fontSize: "13px",
                                        color: "#212529",
                                        textAlign: "center",
                                    }}
                                >
                                    {displayValue(fragranceAnalysisSummary?.['Perc Frag Atts'] ??
                                        fragranceAnalysisSummary?.percFragAtts)}
                                </td>
                                <td
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        fontSize: "13px",
                                        color: "#212529",
                                        textAlign: "center",
                                    }}
                                >
                                    {displayValue(fragranceAnalysisSummary?.['Top Half Frag Atts'] ??
                                        fragranceAnalysisSummary?.topHalfFragAtts)}
                                </td>
                                <td
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        fontSize: "13px",
                                        color: "#212529",
                                        textAlign: "center",
                                    }}
                                >
                                    {displayValue(fragranceAnalysisSummary?.['R-Sq Frag Atts'] ??
                                        fragranceAnalysisSummary?.rSqFragAtts)}
                                </td>
                                <td
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        fontSize: "13px",
                                        color: "#212529",
                                        textAlign: "center",
                                    }}
                                >
                                    {displayValue(fragranceAnalysisSummary?.['Model (Frag) P-Value'] ??
                                        fragranceAnalysisSummary?.modelFragPValue)}
                                </td>
                                <td
                                    style={{
                                        border: "1px solid #dee2e6",
                                        padding: "10px 12px",
                                        fontSize: "13px",
                                        color: "#212529",
                                        textAlign: "center",
                                    }}
                                >
                                    {displayValue(fragranceAnalysisSummary?.['Model (Res) P-Value'] ??
                                        fragranceAnalysisSummary?.modelResPValue)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Typechecking props for the DriverAnalysis
DriverAnalysisHorizontalBarChart.propTypes = {
    variableKpi: PropTypes.array,
    algorithm: PropTypes.string,
    fragranceAnalysisSummary: PropTypes.object,
    isFragranceReport: PropTypes.bool,
};

export default DriverAnalysisHorizontalBarChart;
